import React from 'react'
import { Helmet } from 'react-helmet-async'
import { useLocation } from 'react-router-dom'

// Types
import type { FC } from 'react'

interface SEOProps {
  title?: string
  description?: string
  keywords?: string
  image?: string
  url?: string
  type?: 'website' | 'article' | 'profile'
  locale?: string
  siteName?: string
  author?: string
  publishedTime?: string
  modifiedTime?: string
  section?: string
  tags?: string[]
  noIndex?: boolean
  noFollow?: boolean
  canonical?: string
  alternateLanguages?: Array<{
    hrefLang: string
    href: string
  }>
  schema?: object
}

const SEO: FC<SEOProps> = ({
  title,
  description = 'ค้นหาบริษัทรักษาความปลอดภัยที่เชื่อถือได้ในประเทศไทย พร้อมข้อมูลติดต่อ บริการ และแผนที่ตำแหน่ง',
  keywords = 'รักษาความปลอดภัย, บริษัทรักษาความปลอดภัย, security company, ยาม, รปภ., ไทย, Thailand',
  image = 'https://thaisecurity.directory/og-image.jpg',
  url,
  type = 'website',
  locale = 'th_TH',
  siteName = 'ไทยเซ็กคิวริตี้ไดเรกทอรี่',
  author = 'Thai Security Directory',
  publishedTime,
  modifiedTime,
  section,
  tags,
  noIndex = false,
  noFollow = false,
  canonical,
  alternateLanguages,
  schema
}) => {
  const location = useLocation()

  // Generate dynamic values
  const defaultTitle = 'ไทยเซ็กคิวริตี้ไดเรกทอรี่ - ค้นหาบริษัทรักษาความปลอดภัย'
  const pageTitle = title ? `${title} | ${siteName}` : defaultTitle
  const currentUrl = url || `https://thaisecurity.directory${location.pathname}`
  const canonicalUrl = canonical || currentUrl

  // Robots content
  const robotsContent = [
    noIndex ? 'noindex' : 'index',
    noFollow ? 'nofollow' : 'follow'
  ].join(', ')

  // Default structured data
  const defaultSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: siteName,
    alternateName: 'Thai Security Directory',
    url: 'https://thaisecurity.directory/',
    description: description,
    inLanguage: 'th',
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://thaisecurity.directory/search?q={search_term_string}',
      'query-input': 'required name=search_term_string'
    }
  }

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{pageTitle}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta name="author" content={author} />
      <meta name="robots" content={robotsContent} />

      {/* Canonical URL */}
      <link rel="canonical" href={canonicalUrl} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:url" content={currentUrl} />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:locale" content={locale} />

      {/* Article specific */}
      {type === 'article' && (
        <>
          {author && <meta property="article:author" content={author} />}
          {publishedTime && <meta property="article:published_time" content={publishedTime} />}
          {modifiedTime && <meta property="article:modified_time" content={modifiedTime} />}
          {section && <meta property="article:section" content={section} />}
          {tags && tags.map(tag => (
            <meta key={tag} property="article:tag" content={tag} />
          ))}
        </>
      )}

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
      <meta name="twitter:url" content={currentUrl} />

      {/* Alternate Languages */}
      {alternateLanguages?.map(({ hrefLang, href }) => (
        <link key={hrefLang} rel="alternate" hrefLang={hrefLang} href={href} />
      ))}

      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(schema || defaultSchema)}
      </script>

      {/* Additional Meta Tags for Thai content */}
      <meta httpEquiv="Content-Language" content="th" />
      <meta name="format-detection" content="telephone=yes" />
      <meta name="format-detection" content="address=yes" />

      {/* Performance and Loading */}
      <meta name="preload" content="true" />
      <meta httpEquiv="x-dns-prefetch-control" content="on" />

      {/* Security */}
      <meta httpEquiv="X-Content-Type-Options" content="nosniff" />
      <meta httpEquiv="X-Frame-Options" content="DENY" />
      <meta httpEquiv="X-XSS-Protection" content="1; mode=block" />

      {/* Theme and Appearance */}
      <meta name="theme-color" content="#1e40af" />
      <meta name="msapplication-TileColor" content="#1e40af" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="default" />
      <meta name="apple-mobile-web-app-title" content={siteName} />

      {/* Viewport and Mobile */}
      <meta name="viewport" content="width=device-width, initial-scale=1.0, viewport-fit=cover" />
      <meta name="mobile-web-app-capable" content="yes" />
    </Helmet>
  )
}

export default SEO